/* ==========================================================================
   AU Easify: ONE behaviour layer for Easify's option widget everywhere it
   renders (William, Sep 30): the PDP customize dialog's options area and
   Easify's in-cart "Edit options" popup. Styles: assets/au-easify.css.

   1. Widget enhancements, run on every .au-ez root:
      - "Choose Your Symbol" label over category tabs + symbol tray
      - CONFIRMATION checkbox -> confirmation card ([data-au-confirm])
      - group titles: FRONT/BACK/PLAQUE n -> navy side tag, "(SAVE N%)" -> "Saving N%"
   2. The edit popup, made to match the customize dialog: two-column
      product panel, fee bar with the delivery countdown, help note,
      "Save Changes", required-checkbox guard, leave warning.
   Hooks use Easify's readable class names only (their po-XXXXXXXX
   hashes change between renders).
   ========================================================================== */
(function () {
  if (window.AUEZ) return;

  /* ---------- helpers ---------- */
  function each(root, sel, fn) {
    Array.prototype.forEach.call(root.querySelectorAll(sel), fn);
  }
  function leaf(el) {
    return !!el && el.matches('.po-option-root') && !el.querySelector('.po-option-root');
  }

  /* Required checkbox options with nothing ticked (Easify gives them no
     native `required` and shows its error only after interaction). */
  function uncheckedRequiredBoxes(root) {
    return Array.prototype.filter.call(root.querySelectorAll('.po-checkbox-root'), function (cb) {
      if (!cb.getClientRects().length) return false;
      var labelRoot = cb.querySelector('.po-label-root');
      if (!labelRoot || !labelRoot.querySelector('.po-label-required')) return false;
      return !cb.querySelector('input[type="checkbox"]:checked');
    });
  }
  function flagMissing(root) {
    var boxes = uncheckedRequiredBoxes(root);
    boxes.forEach(function (cb) {
      var opt = cb.closest('.po-option-root');
      if (opt) opt.setAttribute('data-au-missing', '');
    });
    if (boxes.length) boxes[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
    return boxes.length > 0;
  }
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.type === 'checkbox' && t.checked && t.closest) {
      var opt = t.closest('[data-au-missing]');
      if (opt) opt.removeAttribute('data-au-missing');
    }
  });

  /* ---------- 1. widget enhancements ---------- */

  /* Category tabs + symbol tray are ONE choice, so the tabs' label reads
     "Choose Your Symbol" (display only; the cart property keeps
     "Symbol Category"). Detected structurally. */
  function relabelSymbols(root) {
    each(root, '.po-buttons-optionGroup', function (g) {
      if (g.children.length < 4) return;
      var opt = g.closest('.po-option-root');
      var next = opt ? opt.nextElementSibling : null;
      if (!leaf(opt) || !leaf(next) || !next.querySelector('.po-imageSwatches-root')) return;
      var label = opt.querySelector('.po-label-label');
      var orig = label ? label.firstElementChild : null;
      if (!orig || orig.classList.contains('au-label-alt') || label.querySelector('.au-label-alt')) return;
      orig.setAttribute('data-au-label-hidden', '');
      var alt = document.createElement('span');
      alt.className = 'au-label-alt';
      alt.setAttribute('aria-hidden', 'true');
      alt.textContent = 'Choose Your Symbol';
      orig.insertAdjacentElement('afterend', alt);
    });
  }

  /* A checkbox option whose label starts "Confirmation" (any case)
     renders as the confirmation card. Detected by label: Easify exposes
     no option name, and option ids are random per profile. */
  var CONFIRM_RE = /^\s*confirmation\b/i;
  function markConfirm(root) {
    each(root, '.po-checkbox-root', function (cb) {
      var opt = cb.closest('.po-option-root');
      var lab = cb.querySelector('.po-label-label');
      if (!opt || !lab) return;
      var is = CONFIRM_RE.test(lab.textContent || '');
      if (is && !opt.hasAttribute('data-au-confirm')) opt.setAttribute('data-au-confirm', '');
      else if (!is && opt.hasAttribute('data-au-confirm')) opt.removeAttribute('data-au-confirm');
    });
  }

  /* Group titles: leading side word -> navy tag, trailing "(Save N%)" /
     "(N% OFF)" -> red "Saving N%" chip ("Saving": the discount reads as
     already claimed; the Back group only mounts while it's true).
     Easify's text node stays (React owns it), visually hidden; the
     rebuild is aria-hidden so screen readers read the title once. */
  /* Sides, plus numbered plaques for plaque-only profiles ("PLAQUE 1"). */
  var SIDE = /^(front|back|left|right|plaque\s*\d+)\b\s*/i;
  var SAVE = /\s*\(\s*(?:save\s+(\d+)\s*%|(\d+)\s*%\s*off)\s*\)\s*$/i;
  function cap(w) {
    var m = w.match(/^plaque\s*(\d+)$/i);
    if (m) return 'Plaque ' + m[1];
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }
  function decorateTitles(root) {
    each(root, '.po-group-root > .po-field-root > .po-label-root .po-label-labelContent', function (lc) {
      var text = (lc.textContent || '').replace(/\s+/g, ' ').trim();
      var mine = lc.nextElementSibling && lc.nextElementSibling.classList.contains('au-gtitle') ? lc.nextElementSibling : null;
      if (lc.getAttribute('data-au-gt-src') === text && (mine || !lc.hasAttribute('data-au-gt'))) return;
      if (mine) mine.remove();
      lc.removeAttribute('data-au-gt');
      lc.setAttribute('data-au-gt-src', text);
      var side = text.match(SIDE);
      var save = text.match(SAVE);
      if (!side && !save) return;
      var rest = text;
      if (save) rest = rest.replace(SAVE, '');
      if (side) rest = rest.replace(SIDE, '');
      rest = rest.trim();
      var out = document.createElement('span');
      out.className = 'au-gtitle';
      out.setAttribute('aria-hidden', 'true');
      if (side) {
        var tag = document.createElement('span');
        tag.className = 'au-gtitle__side';
        tag.textContent = cap(side[1]);
        out.appendChild(tag);
      }
      var body = document.createElement('span');
      body.className = 'au-gtitle__text';
      body.textContent = side ? rest : rest.charAt(0).toUpperCase() + rest.slice(1);
      out.appendChild(body);
      if (save) {
        var chip = document.createElement('span');
        chip.className = 'au-pdetails__save au-gtitle__save';
        chip.textContent = 'Saving ' + (save[1] || save[2]) + '%';
        out.appendChild(chip);
      }
      lc.setAttribute('data-au-gt', '');
      lc.insertAdjacentElement('afterend', out);
    });
  }

  function enhance(root) {
    relabelSymbols(root);
    markConfirm(root);
    decorateTitles(root);
  }

  /* ---------- 2. the in-cart edit popup ---------- */

  var editKey = null; /* cart line key of the Edit Details link last clicked */
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('.po-editOptionsDialog-label');
    if (!link) return;
    var tr = link.closest('[data-key]');
    editKey = tr ? tr.getAttribute('data-key') : null;
  }, true);

  function tpl(id) {
    var t = document.getElementById(id);
    return t && t.content ? t.content.cloneNode(true) : null;
  }
  var BENEFITS = null;
  function benefit(name) {
    if (!BENEFITS) {
      try { BENEFITS = JSON.parse((document.getElementById('au-set-benefits') || {}).textContent || '{}'); } catch (e) { BENEFITS = {}; }
    }
    return BENEFITS[name] || '';
  }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function imgAt(src, w) {
    if (!src) return '';
    return /[?&]width=\d+/.test(src) ? src.replace(/([?&]width=)\d+/, '$1' + w) : src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  /* The cart line being edited: { item (cart.js), row (tr) }. */
  function editLine() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var item = null;
        (cart.items || []).forEach(function (it) { if (it.key === editKey) item = it; });
        var row = editKey ? document.querySelector('tr[data-key="' + editKey.replace(/"/g, '') + '"]') : null;
        return { item: item, row: row };
      })
      .catch(function () { return { item: null, row: null }; });
  }

  /* Product panel (desktop) + compact product row (phone), built from the
     cart line in the PDP panel's exact markup and classes. */
  function buildPanel(line) {
    var item = line.item;
    if (!item) return null;
    var pack = '';
    (item.options_with_values || []).forEach(function (o) { if (o.name === 'Pack') pack = o.value; });
    var setName = pack, contents = '';
    var m = pack.match(/^(.*?)\s*\((.*)\)\s*$/);
    if (m) { setName = m[1]; contents = m[2]; }
    else if (pack) { contents = pack; }
    var title = item.product_title || '';
    var design = '';
    (item.options_with_values || []).forEach(function (o) { if (o.name === 'Design' || o.name === 'Color') design = o.value; });
    var fullTitle = design ? title + ' - ' + design : title;
    var src = item.image || (line.row && line.row.querySelector('img') ? line.row.querySelector('img').currentSrc : '');

    var wrap = el('div', 'au-ezp__summary');
    /* compact row (phones) */
    var row = el('div', 'au-pdetails__customize-product');
    if (src) {
      var fr = el('span', 'au-pdetails__customize-product-frame');
      var im = el('img'); im.src = imgAt(src, 160); im.alt = ''; im.width = 80; im.height = 80;
      fr.appendChild(im); row.appendChild(fr);
    }
    var info = el('span', 'au-pdetails__customize-product-info');
    info.appendChild(el('span', 'au-pdetails__customize-product-name', fullTitle));
    if (pack) info.appendChild(el('span', 'au-pdetails__customize-product-contents', contents && setName !== contents ? setName + ': ' + contents : setName));
    row.appendChild(info);
    wrap.appendChild(row);

    /* panel (desktop) */
    var panel = el('div', 'au-pdetails__customize-panel');
    if (src) {
      var pf = el('span', 'au-pdetails__customize-panel-frame');
      var pi = el('img'); pi.src = imgAt(src, 800); pi.alt = ''; pi.width = 400; pi.height = 400;
      pf.appendChild(pi); panel.appendChild(pf);
    }
    panel.appendChild(el('span', 'au-pdetails__customize-panel-name', fullTitle));
    if (pack) {
      panel.appendChild(el('span', 'au-pdetails__customize-panel-set', setName));
      var b = benefit(setName);
      if (b) panel.appendChild(el('span', 'au-pdetails__customize-panel-benefit', b));
      if (contents) {
        var items = el('span', 'au-pdetails__customize-panel-items');
        items.appendChild(el('span', 'au-pdetails__bundle-itemshead', "What's Included:"));
        contents.split(',').forEach(function (part) {
          var t = part.trim();
          var first = t.split(' ')[0];
          var q = parseInt(first, 10);
          var rowEl = el('span', 'au-pdetails__bundle-item');
          if (q > 0 && String(q) === first) {
            rowEl.appendChild(el('span', 'au-pdetails__bundle-item-qty', q + 'x'));
            rowEl.appendChild(el('span', 'au-pdetails__bundle-item-name', t.slice(first.length).trim()));
          } else {
            rowEl.appendChild(el('span', 'au-pdetails__bundle-item-name', t));
          }
          items.appendChild(rowEl);
        });
        panel.appendChild(items);
      }
    }
    var trust = tpl('au-tpl-trust');
    if (trust) panel.appendChild(trust);
    wrap.appendChild(panel);
    return wrap;
  }

  /* Fee bar: Easify's summary mirrored (theirs hidden), per unit above
     qty 1, and the shared delivery countdown (assets/au-ship.js). */
  function buildFee() {
    var p = el('p', 'au-pdetails__customize-fee au-ezp__fee');
    p.hidden = true;
    var price = el('span', 'au-pdetails__customize-fee-price', 'Personalized engraving: ');
    price.appendChild(el('strong', null, ''));
    var unit = el('span', null, ' per unit'); unit.hidden = true;
    price.appendChild(unit);
    p.appendChild(price);
    p.appendChild(document.createTextNode(' '));
    p.appendChild(el('span', 'au-pdetails__customize-fee-value', ''));
    return p;
  }
  function syncFee(dlg, qty) {
    var fee = dlg.querySelector('.au-ezp__fee');
    if (!fee) return;
    var src = dlg.querySelector('.po-priceSummary-root .po-priceSummary-price');
    var text = src ? (src.textContent || '').trim() : '';
    var show = /\d/.test(text) && !/^\$?0(\.00)?$/.test(text);
    var strong = fee.querySelector('strong');
    if (show && strong.textContent !== text) strong.textContent = text;
    fee.querySelector('strong + span').hidden = !(qty > 1);
    var ship = fee.querySelector('.au-pdetails__customize-fee-value');
    var t = 'Still ships the same day when ordered by 1 PM Eastern.';
    if (window.AUShip) {
      var s = window.AUShip.compute();
      if (s.minsLeft > 0 && s.minsLeft < 6000) {
        var h = Math.floor(s.minsLeft / 60), mm = s.minsLeft % 60;
        t = 'Still ships ' + s.shipDayName + ' when ordered within ' + (h ? h + 'h ' : '') + mm + 'm.';
      }
    }
    if (ship.textContent !== t) ship.textContent = t;
    fee.hidden = !show;
  }

  /* Comparable snapshot of the popup's choices (dirty check). */
  function snapshot(root) {
    var parts = [];
    each(root, 'input, textarea, select', function (i) {
      if (i.type === 'hidden') return;
      parts.push(i.type === 'checkbox' || i.type === 'radio' ? (i.checked ? '1' : '0') : i.value);
    });
    each(root, '.po-buttonOption-selected, .po-imageOption-checked, .po-toggle-on', function (n) {
      parts.push((n.textContent || '').trim() || (n.querySelector('img') || {}).alt || 'on');
    });
    return parts.join('\u0001');
  }

  /* Leave warning (same component and classes as the PDP's). */
  var leaveDlg = null;
  function leaveDialog() {
    if (leaveDlg) return leaveDlg;
    leaveDlg = el('dialog', 'au-pdetails__leave');
    leaveDlg.setAttribute('aria-labelledby', 'au-ezp-leave-h');
    leaveDlg.innerHTML =
      '<button class="au-pdetails__customize-close au-ezp__leave-x" type="button" data-au-ezp-stay aria-label="Back to editing">' +
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button>' +
      '<h2 class="au-pdetails__leave-heading" id="au-ezp-leave-h">Leave Without Saving?</h2>' +
      '<p class="au-pdetails__leave-text">Your changes will be lost. Your original engraving stays as it was.</p>' +
      '<button class="au-pdetails__cta" type="button" data-au-ezp-stay>Keep Editing</button>' +
      '<button class="au-pdetails__leave-go" type="button" data-au-ezp-go>' +
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      'Leave Without Saving</button>';
    document.body.appendChild(leaveDlg);
    leaveDlg.addEventListener('click', function (e) {
      if (e.target === leaveDlg || e.target.closest('[data-au-ezp-stay]')) { leaveDlg.close(); return; }
      if (e.target.closest('[data-au-ezp-go]')) {
        leaveDlg.close();
        var d = leaveDlg.__for;
        if (d) {
          d.__auAllowClose = true;
          var x = d.querySelector('.po-dialog-closeButton');
          if (x) x.click();
          setTimeout(function () { d.__auAllowClose = false; }, 300);
        }
      }
    });
    return leaveDlg;
  }
  function askLeave(dlg) {
    var l = leaveDialog();
    l.__for = dlg;
    if (!l.open) l.showModal();
    var stay = l.querySelector('.au-pdetails__cta');
    if (stay) stay.focus();
  }
  function dirty(dlg) {
    var body = dlg.querySelector('.po-dialog-body');
    return !!(body && dlg.__auSnap != null && snapshot(body) !== dlg.__auSnap);
  }

  function setupPopup(dlg) {
    if (dlg.__auEzp) return;
    var body = dlg.querySelector('.po-dialog-body');
    if (!body || !dlg.querySelector('.po-editOptionsContent-btnConfirm')) return;
    dlg.__auEzp = true;
    dlg.classList.add('au-ez-popup');
    body.classList.add('au-ez');

    editLine().then(function (line) {
      dlg.__auQty = line.item ? line.item.quantity : 1;
      var summary = buildPanel(line);
      if (summary) dlg.insertBefore(summary, body);
      dlg.insertBefore(buildFee(), body);
      /* The note ends the scrolling content, just above the pinned
         Save Changes footer (which lives inside Easify's form). */
      var note = tpl('au-tpl-note');
      var footer = body.querySelector('.po-editOptionsContent-buttonGroups');
      var noteEl = note ? note.querySelector('.au-pdetails__customize-note') : null;
      if (noteEl && !body.querySelector('.au-pdetails__customize-note')) {
        if (footer) footer.parentNode.insertBefore(noteEl, footer);
        else {
          var form = body.querySelector('form');
          if (form) form.insertAdjacentElement('afterend', noteEl);
        }
      }
      syncFee(dlg, dlg.__auQty);
      /* Baseline for the leave warning once Easify has restored the
         saved values. */
      setTimeout(function () { dlg.__auSnap = snapshot(body); }, 600);
    });

    /* Required checkbox guard on Save Changes. A save that goes through
       makes Easify RELOAD the page (verified Sep 30), which closed the
       cart drawer: flag it so the drawer reopens on the reloaded page. */
    dlg.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.po-editOptionsContent-btnConfirm');
      if (!btn) return;
      if (uncheckedRequiredBoxes(body).length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        flagMissing(body);
        return;
      }
      try { sessionStorage.setItem(REOPEN_KEY, String(Date.now())); } catch (err) {}
    }, true);

    /* Leave warning: X, backdrop and Esc ask first when anything changed. */
    dlg.addEventListener('click', function (e) {
      if (dlg.__auAllowClose) return;
      var x = e.target.closest && e.target.closest('.po-dialog-closeButton');
      var backdrop = e.target === dlg;
      if (!x && !backdrop) return;
      if (!dirty(dlg)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      askLeave(dlg);
    }, true);
    dlg.addEventListener('cancel', function (e) {
      if (dlg.__auAllowClose || !dirty(dlg)) return;
      e.preventDefault();
      askLeave(dlg);
    });
  }

  /* Footer always pinned at the bottom (William, Sep 30). Easify's footer
     lives inside its form, so it can only stick while the options are
     taller than the popup; with short content it floated up. The spare
     height goes above the help note, so note + Save Changes sit at the
     bottom together. */
  function pinFooter(dlg) {
    var body = dlg.querySelector('.po-dialog-body');
    var footer = body && body.querySelector('.po-editOptionsContent-buttonGroups');
    if (!footer) return;
    var spacer = body.querySelector('.au-pdetails__customize-note') || footer;
    spacer.style.marginTop = '';
    /* scrollHeight never drops below clientHeight, so measure the
       footer's real position against the body's inner bottom. */
    if (body.scrollHeight > body.clientHeight + 1) return;
    var padB = parseFloat(getComputedStyle(body).paddingBottom) || 0;
    var spare = Math.floor(body.getBoundingClientRect().bottom - padB - footer.getBoundingClientRect().bottom);
    if (spare > 0) {
      var base = parseFloat(getComputedStyle(spacer).marginTop) || 0;
      spacer.style.marginTop = (base + spare) + 'px';
    }
  }

  /* ---------- defaults for choices that mount empty in the popup ----------
     Switching a section on inside Easify's edit popup ("Engrave 4 Keepsake
     Urns?") mounts its choices (Front & Back / Front Only, Text / Symbol)
     with NOTHING selected; on the PDP the same choices mount with the
     profile default (William, Sep 30). Defaults are recorded from the PDP
     widget at first sight (option ids are identical in both, verified
     Sep 30) and kept in localStorage; the popup selects the recorded
     default, or the first choice if none was seen. Only ever fills an
     EMPTY choice, never changes one the customer or the saved line set. */
  var DEF_KEY = 'au-ez-defaults';
  var defaults = null;
  function loadDefaults() {
    if (defaults) return defaults;
    try { defaults = JSON.parse(localStorage.getItem(DEF_KEY) || '{}') || {}; } catch (e) { defaults = {}; }
    return defaults;
  }
  function saveDefaults() {
    try { localStorage.setItem(DEF_KEY, JSON.stringify(defaults)); } catch (e) {}
  }
  function choiceItems(opt) {
    return Array.prototype.filter.call(opt.querySelectorAll('.po-optionItem-item, .po-optionSwatch-root'), function (i) {
      return i.closest('.po-option-root') === opt && !(i.matches('.po-optionItem-item') && i.closest('.po-optionSwatch-root'));
    });
  }
  function itemLabel(i) {
    var img = i.querySelector('img');
    return (i.textContent || '').trim() || (img && (img.alt || img.title)) || '';
  }
  function itemSelected(i) {
    return /selected/i.test(i.className) || /selected/i.test(i.parentElement.className) || !!i.querySelector('[class*="elected"], .po-imageOption-checked');
  }
  function recordDefaults(root) {
    var d = loadDefaults(), changed = false;
    each(root, '.po-option-root[data-option-id]', function (opt) {
      if (!leaf(opt) || !opt.getClientRects().length) return;
      var id = opt.getAttribute('data-option-id');
      if (id in d) return;
      var items = choiceItems(opt);
      if (!items.length) return;
      var picked = items.filter(itemSelected);
      d[id] = picked.length ? itemLabel(picked[0]) : '';
      changed = true;
    });
    if (changed) saveDefaults();
  }
  /* One fill per tick: Easify re-renders dependants after each choice. */
  function fillDefaults(root) {
    var d = loadDefaults();
    var opts = root.querySelectorAll('.po-option-root[data-option-id]');
    for (var n = 0; n < opts.length; n++) {
      var opt = opts[n];
      if (!leaf(opt) || !opt.getClientRects().length) continue;
      var items = choiceItems(opt);
      if (!items.length || items.some(itemSelected)) continue;
      var want = d[opt.getAttribute('data-option-id')];
      if (want === '') continue; /* the PDP default is "nothing selected" */
      var target = null;
      if (want) items.forEach(function (i) { if (!target && itemLabel(i) === want) target = i; });
      target = target || items[0];
      target.click();
      return true;
    }
    return false;
  }

  /* ---------- driver ---------- */
  function run() {
    each(document, '.easify.portal dialog.po-dialog-dialog', function (d) {
      if (d.querySelector('.po-editOptionsContent-btnConfirm')) setupPopup(d);
    });
    each(document, '.au-ez', function (root) {
      enhance(root);
      if (root.matches('[data-au-easify-slot]')) recordDefaults(root);
      var dlg = root.closest('.au-ez-popup');
      if (dlg) {
        if (dlg.__auSnap != null && fillDefaults(root)) queue();
        pinFooter(dlg);
        syncFee(dlg, dlg.__auQty || 1);
        dlg.toggleAttribute('data-au-ezp-invalid', uncheckedRequiredBoxes(root).length > 0);
      }
    });
  }
  var queued = false;
  function queue() {
    if (queued) return;
    queued = true;
    setTimeout(function () { queued = false; run(); }, 60);
  }
  var REOPEN_KEY = 'au-ez-reopen-cart';
  function reopenCartAfterSave() {
    var t = 0;
    try { t = parseInt(sessionStorage.getItem(REOPEN_KEY), 10) || 0; sessionStorage.removeItem(REOPEN_KEY); } catch (e) { return; }
    if (!t || Date.now() - t > 20000) return;
    var tries = 0;
    (function openWhenReady() {
      var cdc = document.querySelector('cart-drawer-component');
      var drawer = cdc && cdc.closest('theme-drawer');
      if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
      if (++tries < 40) setTimeout(openWhenReady, 150);
    })();
  }

  function start() {
    reopenCartAfterSave();
    window.addEventListener('resize', queue, { passive: true });
    new MutationObserver(queue).observe(document.body, { childList: true, subtree: true, characterData: true });
    run();
    setInterval(run, 10000); /* countdown tick */
  }
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);

  window.AUEZ = {
    uncheckedRequiredBoxes: uncheckedRequiredBoxes,
    flagMissing: flagMissing,
    enhance: enhance
  };
})();
